import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { AuthApiService } from 'src/app/core/services/auth-api.service';
import { PromptUpdateService } from 'src/app/core/services/prompt-update.service';
import { httpErrorMsgs } from 'src/app/core/utils/htp-error';
import { SupportService } from 'src/app/shared/services/support/support.service';

// Salvaguarda: se a navegação pós-login não concluir nesse prazo (ex.: guard
// pendurado aguardando dados da sessão), o botão é liberado para nova tentativa
// em vez de ficar preso em "Entrando...".
const NAV_SAFETY_TIMEOUT_MS = 15000;

// Instrumentação: se o clique em "Entrar" não resultar em next/error do
// authService.login() nesse prazo, captura um diagnóstico no Sentry com os
// breadcrumbs da etapa em que travou. Só diagnóstico: não altera o loading.
// Fica ACIMA do timeout da única etapa bloqueante (POST de login, 15s — o
// signIn do Firebase roda em background e não segura o botão) para disparar
// apenas em travamento genuíno — não como corrida com esse timeout
// (GESTOR-CLIENT-TW). Só reporta se algo escapar de todas as salvaguardas.
const LOGIN_STUCK_DIAGNOSTIC_MS = 20000;

@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup;
  public error: string;
  public loading: boolean = false;
  public showPwd: boolean = false;
  public supportLoading$ = this.supportService.loading$;
  public supportType$ = this.supportService.preLoginSupportType$;
  public supportReady$ = this.supportService.preLoginReady$;
  public contactModalVisible = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthApiService,
    private fb: UntypedFormBuilder,
    private promptUpdateService: PromptUpdateService,
    public supportService: SupportService,
  ) { }

  public openSupport(): void {
    this.supportService.openPreLoginWidget();
  }

  public openContactModal(): void {
    this.contactModalVisible = true;
  }

  public ngOnInit(): void {
    this.form = this.fb.group({
      username: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required]],
    });

    this.authService.lastLogin().then(username => {
      if (username) {
        this.form.patchValue({ username: atob(username) });
      }
    });

    this.supportService.checkPreLoginSupport();

    // Guard de auto-update: impede o PromptUpdateService de recarregar a
    // página (inclusive no visibilitychange) com credenciais digitadas ou
    // submit em andamento.
    this.promptUpdateService.setAutoApplyGuard(() => !this.loading && this.form.pristine);
    this.checkForAppUpdate();
  }

  public ngOnDestroy(): void {
    this.promptUpdateService.setAutoApplyGuard(null);
  }

  /**
   * A tela de login ficava fora do ciclo de verificação do Service Worker
   * (PromptUpdateService só era instanciado no layout autenticado): uma aba
   * aberta antes de um deploy continuava rodando bundle velho — inclusive sem
   * os próprios fixes do login. Aqui é seguro atualizar automaticamente,
   * desde que o usuário ainda não tenha interagido com o formulário.
   */
  private checkForAppUpdate(): void {
    this.promptUpdateService.checkForUpdate().then(hasUpdate => {
      if (hasUpdate && !this.loading && this.form.pristine) {
        this.promptUpdateService.applyUpdate();
      }
    }).catch(() => undefined);
  }

  public onSubmit() {
    if (this.loading) { return; }

    this.error = null;
    this.loading = true;

    const value = this.form.value;
    Sentry.addBreadcrumb({ category: 'login-flow', message: 'onSubmit:start' });

    let settled = false;
    const stuckDiagnostic = setTimeout(() => {
      if (!settled) {
        Sentry.captureMessage('Login travado (diagnóstico) - sem next/error após o prazo', {
          level: 'warning',
          tags: { component: 'login', action: 'onSubmit' },
        });
      }
    }, LOGIN_STUCK_DIAGNOSTIC_MS);

    this.authService.login(value.username, value.password).subscribe(ok => {
      settled = true;
      clearTimeout(stuckDiagnostic);
      Sentry.addBreadcrumb({ category: 'login-flow', message: 'onSubmit:next', data: { ok } });
      if (ok) {
        const navigate = this.route.snapshot.queryParams['returnUrl'];
        this.redirectAfterLogin(!navigate ? '/' : navigate);
      } else {
        this.loading = false;
        this.loginError('Usuário e/ou senha inválidos.');
      }
    }, err => {
      settled = true;
      clearTimeout(stuckDiagnostic);
      Sentry.addBreadcrumb({ category: 'login-flow', message: 'onSubmit:error' });
      this.loading = false;
      this.loginError(err);
    });
  }

  private redirectAfterLogin(target: string): void {
    // Mantém o loading durante a navegação (evita reabilitar o botão), mas
    // com salvaguardas: se a navegação for cancelada/falhar (guard nega) ou
    // não concluir a tempo (guard pendurado), o botão é liberado para nova
    // tentativa em vez de congelar em "Entrando...".
    Sentry.addBreadcrumb({ category: 'login-flow', message: 'redirectAfterLogin:start', data: { target } });

    const onNavStuck = () => {
      if (this.loading) {
        Sentry.addBreadcrumb({ category: 'login-flow', message: 'redirectAfterLogin:stuck-or-failed' });
        this.loading = false;
        this.loginError('Não foi possível abrir o sistema. Tente novamente.');
      }
    };

    const safety = setTimeout(onNavStuck, NAV_SAFETY_TIMEOUT_MS);

    this.router.navigateByUrl(target).then(success => {
      clearTimeout(safety);
      // Em caso de sucesso o componente é destruído; só tratamos a falha.
      if (!success) {
        onNavStuck();
      }
    }).catch(() => {
      clearTimeout(safety);
      onNavStuck();
    });
  }

  public loginError(error: Error | string) {
    if (typeof (error) === 'string') {
      this.error = <string>error;
      return;
    }

    const htpErros = httpErrorMsgs(<Error>error, {
      email: 'E-mail',
      password1: 'Senha',
      password2: 'Confirmação da Senha',
      change_code: 'Código de Alteração'
    });

    this.error = '';
    htpErros.forEach(err => {
      const msg = err.title ? `${err.title}: ${err.msg}` : err.msg;
      this.error += msg + '\n';
    });
  }

  public forgotPassword(): void {
    this.router.navigateByUrl('/forgot-password');
  }

}
